import React, { FC, ReactNode, useId } from 'react';
import { LinkProps } from 'react-router-dom';

import { TemplateBasicFragment } from '../../generated/graphql';
import useIsMobile from '../../hooks/useIsMobile';
import translate from '../../i18n/translate';
import ComposeModalTemplate from '../Compose/ComposeModal/ComposeModalTemplate';
import { handleTemplateSelect } from '../Compose/helpers';
import TopicTemplateSelection from '../MUI/Compose/TopicTemplateSelection';
import MetaContainerPopup from '../MetaContainerPopup';

import { getTemplateTitle, getWarningTitle } from './helpers';

type Props = {
  className?: string;
  dataTemplateAll: TemplateBasicFragment[];
  dataTemplateCurrent: TemplateBasicFragment['id'] | null;
  dataTemplateSaved: TemplateBasicFragment['id'] | null;
  dataSearchValue: string;
  inputCustom?: ReactNode;
  isActive: boolean;
  isDefaultDraftChanged: boolean;
  isDraftChanged: boolean;
  isLoading: boolean;
  linkToggleProps: LinkProps;
  onSearch: (term: string) => void;
  requestTemplateChange: (templateId: TemplateBasicFragment['id']) => void;
  requestTemplateClose?: () => void;
  requestTemplateRemove: () => void;
  requestTemplateSave: () => void;
};

/**
 * Template popup for the compose block
 *
 * @param props                       Props passed to the component
 * @param props.className             styled-components generated class name, needed for styling
 * @param props.dataTemplateAll       All available templates
 * @param props.dataTemplateCurrent   Currently selected template
 * @param props.dataTemplateSaved     Currently saved template
 * @param props.dataSearchValue       <input type="search" /> value attribute
 * @param props.inputCustom           Component to use for overview in mobile view
 * @param props.isActive              Whether the component should be visible
 * @param props.isDefaultDraftChanged If the draft with default values has been changed
 * @param props.isLoading             Whether the data is still being loaded
 * @param props.linkToggleProps       Props for the toggle link
 * @param props.onSearch              Callback to invoke when search term is changed
 * @param props.requestTemplateChange Request that selected template is changed
 * @param props.requestTemplateClose  Request to close template popup
 * @param props.requestTemplateRemove Request that current template is set to null
 * @param props.requestTemplateSave   Request that the current template is saved
 * @returns                           The component itself
 */
const MetaTemplate: FC<Props> = ({
  className,
  dataSearchValue,
  dataTemplateAll,
  dataTemplateCurrent,
  dataTemplateSaved,
  inputCustom,
  isActive,
  isDefaultDraftChanged,
  isLoading,
  linkToggleProps,
  onSearch,
  requestTemplateChange,
  requestTemplateClose,
  requestTemplateRemove,
  requestTemplateSave,
}) => {
  const isMobile = useIsMobile();
  /**
   * We need to check if currently selected template is equal to draft/saved template
   * so we can disable save button if they are equal.
   */
  const isSaveDisabled = dataTemplateCurrent === dataTemplateSaved;

  const idForm = useId();
  const idHeading = useId();

  // Popup top buttons' labels
  const labelAction = translate('GENERAL__RESET');
  const labelActionColor = 'var(--color-button-danger)';
  const labelConfirm = translate('GENERAL__SAVE');
  const labelClose = translate('GENERAL__CLOSE');

  const textPlaceholder = translate('COMPOSE__PLACEHOLDER__TEMPLATE');
  const textHeading = textPlaceholder;

  const templateTitle = getTemplateTitle(dataTemplateAll, dataTemplateSaved);
  /**
   * @todo When new toast messages are done, add one to to the new compose
   * implementation to notify the user if all data will be overwritten
   */
  const warningTitle = getWarningTitle(dataTemplateCurrent, dataTemplateSaved);

  /**
   * Select template or unselect if it's already selected
   *
   * @param template Template id for selection
   */
  const requestTemplateSelect = (template: TemplateBasicFragment['id']) => {
    handleTemplateSelect({
      currentTemplateId: dataTemplateCurrent,
      newTemplateId: template,
      requestTemplateChange,
      requestTemplateRemove,
      requestTemplateSave,
    });
  };

  if (isMobile) {
    return (
      <MetaContainerPopup
        action={
          dataTemplateCurrent !== null
            ? {
                color: labelActionColor,
                label: labelAction,
                onClick: requestTemplateRemove,
              }
            : null
        }
        className={className}
        idForm={idForm}
        idHeading={idHeading}
        inputCustom={inputCustom}
        isSaveDisabled={isSaveDisabled}
        labelClose={labelClose}
        labelConfirm={labelConfirm}
        linkProps={linkToggleProps}
        onClose={requestTemplateClose}
        placeholder={textPlaceholder}
        shouldHideSave={true}
        shouldShowPopup={isActive}
        textHeading={textHeading}
        value={templateTitle}
        warningTitle={warningTitle}
      >
        <TopicTemplateSelection
          ariaDescribedBy={textPlaceholder}
          ariaLabelledBy={textPlaceholder}
          dataAll={dataTemplateAll}
          dataCurrent={dataTemplateCurrent}
          dataSearchValue={dataSearchValue}
          isLoading={isLoading}
          onSearch={onSearch}
          requestDataSelect={requestTemplateSelect}
          shouldPushToast={isDefaultDraftChanged && dataTemplateAll.length > 0}
          type="template"
        />
      </MetaContainerPopup>
    );
  }

  return (
    <ComposeModalTemplate
      ariaDescribedBy={idHeading}
      ariaLabelledBy={idHeading}
      dataSearchValue={dataSearchValue}
      dataTemplateAll={dataTemplateAll}
      dataTemplateCurrent={dataTemplateCurrent}
      isDefaultDraftChanged={isDefaultDraftChanged}
      isLoading={isLoading}
      onSearch={onSearch}
      requestTemplateChange={requestTemplateChange}
      requestTemplateRemove={requestTemplateRemove}
      requestTemplateSave={requestTemplateSave}
    />
  );
};

export default MetaTemplate;
